import { useQuery } from '@apollo/client';

import type {
  ArticleLocaleMapperQuery,
  ArticleLocaleMapperQueryVariables,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { localeMapper as localeMapperQuery } from 'bundles/unified-seo-content-common/queries/ArticleLocaleMapperQuery';

const DefaultCountry = 'us';

const DefaultLanguage = 'en';

const DefaultCountryCode = 'global';

type Locale = {
  id?: string;
  language: string;
  region: string;
};

export type LocalesDataProps = {
  locales: Locale[];
  defaultRegionCountryCode: string;
};

const useArticleLocalesData = ({ slug, preview = false }: ArticleLocaleMapperQueryVariables): LocalesDataProps => {
  const { data } = useQuery<ArticleLocaleMapperQuery, ArticleLocaleMapperQueryVariables>(localeMapperQuery, {
    variables: { slug, preview },
    // Only send a request through the Preview API if the user can preview
    context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
    errorPolicy: 'all',
  });

  const localeMapper = data?.articleLocaleMapperCollection?.items[0];

  if (!localeMapper) {
    // Not found
    return { locales: [], defaultRegionCountryCode: DefaultCountryCode };
  }

  const defaultRegion = localeMapper.defaultRegion || DefaultCountry;

  const locales =
    localeMapper.referencesCollection?.items.filter(Boolean).map((item) => ({
      id: item?.sys?.id || '',
      language: item?.localizedLanguageCode || DefaultLanguage,
      region: item?.localizedCountryCode || defaultRegion,
    })) ?? [];

  return {
    locales,
    defaultRegionCountryCode: defaultRegion !== DefaultCountry ? defaultRegion.toUpperCase() : DefaultCountryCode,
  };
};

export default useArticleLocalesData;
